import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { Menu, Bell, ChevronDown } from 'lucide-react';

const Header = ({ onMenuClick }) => {
    const navigate = useNavigate();
    const { user, logout } = useAuth(); // Destructure logout from useAuth
    const { notifications, markAsRead } = useNotifications();
    const [showNotifications, setShowNotifications] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);

    // 獲取未讀通知
    const unreadNotifications = notifications.filter(n => !n.is_read);

    // 點擊通知時設為已讀
    const handleNotificationClick = (notificationId) => {
        markAsRead(notificationId);
        setShowNotifications(false);
    };

    // 點擊其他地方時關閉下拉菜單
    useEffect(() => {
        const handleClickOutside = () => {
            setShowNotifications(false);
            setShowUserMenu(false);
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    // 阻止事件冒泡
    const handleDropdownClick = (e) => {
        e.stopPropagation();
    };

    // 處理登出邏輯
    const handleLogout = () => {
        logout(); // Use the logout method from useAuth
        navigate('/login');
    };

    return (
        <header className="bg-white p-4 shadow-sm flex justify-between items-center">
            <div className="flex items-center">
                <button
                    onClick={onMenuClick}
                    className="p-2 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none"
                >
                    <Menu className="w-6 h-6" />
                </button>
                <h2 className="text-xl font-bold ml-4">SmartRide 管理系統</h2>
            </div>

            <div className="flex items-center space-x-4">
                {/* 通知圖標 */}
                <div className="relative" onClick={handleDropdownClick}>
                    <button
                        onClick={() => setShowNotifications(!showNotifications)}
                        className="p-2 rounded-full hover:bg-gray-100 relative"
                    >
                        <Bell className="w-5 h-5 text-gray-500" />
                        {unreadNotifications.length > 0 && (
                            <span className="absolute top-0 right-0 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                                {unreadNotifications.length > 9 ? '9+' : unreadNotifications.length}
                            </span>
                        )}
                    </button>

                    {/* 通知下拉選單 */}
                    {showNotifications && (
                        <div className="absolute right-0 mt-2 w-80 bg-white border rounded-md shadow-lg z-10">
                            <div className="p-3 border-b">
                                <h3 className="font-bold">通知</h3>
                            </div>
                            <div className="max-h-96 overflow-y-auto">
                                {notifications.length === 0 ? (
                                    <div className="p-4 text-center text-gray-500">沒有通知</div>
                                ) : (
                                    notifications.slice(0, 5).map((notification) => (
                                        <div
                                            key={notification.notification_id}
                                            className={`p-3 border-b hover:bg-gray-50 cursor-pointer ${!notification.is_read ? 'bg-blue-50' : ''
                                                }`}
                                            onClick={() => handleNotificationClick(notification.notification_id)}
                                        >
                                            <p className="font-medium">{notification.title}</p>
                                            <p className="text-sm text-gray-600 truncate">{notification.content}</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {new Date(notification.created_at).toLocaleString()}
                                            </p>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="p-2 border-t">
                                <button
                                    onClick={() => navigate('/notifications')}
                                    className="w-full text-center text-blue-500 text-sm py-1 hover:underline"
                                >
                                    查看全部通知
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* 用戶圖標 */}
                <div className="relative" onClick={handleDropdownClick}>
                    <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100"
                    >
                        <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                        <span className="text-sm font-medium">{user ? user.username : '管理員'}</span>
                        <ChevronDown className="w-4 h-4" />
                    </button>

                    {/* 用戶下拉選單 */}
                    {showUserMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-10">
                            <div className="p-3 border-b">
                                <p className="font-medium">{user ? user.username : '管理員'}</p>
                                <p className="text-xs text-gray-500">{user ? user.email : ''}</p>
                            </div>
                            <div className="p-2">
                                <button
                                    onClick={() => navigate('/profile')}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded-md"
                                >
                                    個人資料
                                </button>
                                <button
                                    onClick={() => navigate('/settings')}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded-md"
                                >
                                    設置
                                </button>
                                <div className="border-t my-1"></div>
                                <button
                                    onClick={handleLogout} // Use the new handler
                                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-gray-100 rounded-md"
                                >
                                    登出
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;